/**
 * AUTO-GENERATED FILE - DO NOT EDIT DIRECTLY
 *
 * This file is generated from entity_pivot_questions_to_queries.yaml
 * To update pivot questions, edit the YAML file and run:
 *   npm run codegen:investigator-pivot-questions
 *
 * IMPORTANT: This file should be committed to version control.
 * It is generated at build time but checked in so the application
 * can use it without requiring the YAML parser at runtime.
 *
 * Generated: 2026-01-16T23:18:32.809Z
 */

/* eslint-disable no-useless-escape */

export interface PivotQueryParameter {
  name: string;
  description: string;
}

export interface PivotQuery {
  id: string;
  question: string;
  description: string;
  sources: string[];
  tags?: string[];
  parameters: PivotQueryParameter[];
  query: string;
}

/**
 * Pivot questions and their corresponding LogScale queries
 * @see entity_pivot_questions_to_queries.yaml for source data
 */
export const PIVOT_QUERIES: PivotQuery[] = [
  {
    id: 'entity-ids-alerts-summary',
    question: 'What IDS alerts was this host the source or destination of?',
    description:
      'Summarize Suricata alerts where the entity IP was the source or destination.',
    sources: ['suricata_corelight'],
    parameters: [
      {
        name: 'HOST_IP',
        description:
          'IP address whose associated IDS alerts are being summarized',
      },
    ],
    query: `#path=suricata_corelight
| id.orig_h = \${HOST_IP} OR id.resp_h = \${HOST_IP}
| case {
  id.orig_h = \${HOST_IP}
  | entity_role := "source";
  id.resp_h = \${HOST_IP}
  | entity_role := "destination";
  *
  | entity_role := "unknown";
}
| table(fields=[ts, id.orig_h, id.resp_h, entity_role, alert.category, alert.signature, alert.severity])`,
  },
  {
    id: 'protocol-anomalies-role-breakdown',
    question:
      'What anomalous or non-RFC compliant protocol usage has been observed from this?',
    description: 'Group protocol anomalies by role, analyzer, and reason.',
    sources: ['dpd', 'weird'],
    parameters: [
      {
        name: 'HOST_IP',
        description:
          'IP address whose associated IDS alerts are being summarized',
      },
    ],
    query: `in(#path, values=[dpd, weird])
| id.orig_h=\${HOST_IP} OR id.resp_h=\${HOST_IP}
| case {
   id.orig_h = \${HOST_IP}
   | remote_ip := id.resp_h
   | entity_role := "client";
   id.resp_h = \${HOST_IP}
   | remote_ip := id.orig_h
   | entity_role := "server";
   *
   | remote_ip := "unknown"
   | entity_role := "unknown";
}
| rename(field=[[name, reason], [failure_reason, reason], [source, analyzer]])
| reason!=/^data is missing|^Binpac exception|^.*\.spicy:/
| !in(reason, values=["possible_split_routing", "data_before_established" ])
| groupBy(field=[entity_role, analyzer, reason], function=[count(remote_ip, distinct=true, as=remote_ips), selectLast(ts), count(as="total_event_cnt")])
| rename(field=[[ts, "last_seen"]])
| sort(total_event_cnt, order=desc)`,
  },
  {
    id: 'file-transfers-host',
    question: 'What file transfers has this host been involved in?',
    description:
      'Aggregate file transfer activity by direction, source, and mime type.',
    sources: ['files'],
    parameters: [
      {
        name: 'HOST_IP',
        description:
          'IP address whose associated IDS alerts are being summarized',
      },
    ],
    query: `#path=files
| id.orig_h=\${HOST_IP} OR id.resp_h=\${HOST_IP}
| case {
   id.orig_h = \${HOST_IP} AND is_orig = true AND local_orig = true
   |  transfer_direction := "upload_from_local";
   id.orig_h = \${HOST_IP} AND is_orig = true AND local_orig = false
   |  transfer_direction := "upload_from_external";
   id.orig_h = \${HOST_IP} AND is_orig = false AND local_orig = true
   |  transfer_direction := "download_from_local";
   id.orig_h = \${HOST_IP} AND is_orig = false AND local_orig = false
   |  transfer_direction := "download_from_external";
   id.resp_h = \${HOST_IP} AND is_orig = true AND local_orig = true
   |  transfer_direction := "download_from_local";
   id.resp_h = \${HOST_IP} AND is_orig = true AND local_orig = false
   |  transfer_direction := "download_from_external";
   id.resp_h = \${HOST_IP} AND is_orig = false AND local_orig = true
   |  transfer_direction := "upload_to_local";
   id.resp_h = \${HOST_IP} AND is_orig = false AND local_orig = false
   |  transfer_direction := "upload_to_external";
   *
   | transfer_direction := "unknown";
}
| case {
   id.orig_h = \${HOST_IP}
   | remote_ip := id.resp_h;
   id.resp_h = \${HOST_IP}
   | remote_ip := id.orig_h;
   *
   | remote_ip := "unknown";
}
| groupBy(
   field=[transfer_direction, source, mime_type],
   function=[
       min(ts, as=first_ts),
       max(ts, as=last_ts),
       count(as=transfer_cnt),
       count(filename, distinct=true, as=unique_filenames),
       collect(fields=filename, limit=5),
       sum(total_bytes, as=total_bytes_transferred),
       avg(total_bytes, as=avg_file_size),
       count(remote_ip, distinct=true, as=remote_ips)
   ]
)`,
  },
  {
    id: 'top-hosts-by-connection-count',
    question: 'What are the top hosts this entity communicated with?',
    description:
      'Rank network peers by connection count, describing direction and entity role.',
    sources: ['conn'],
    parameters: [
      {
        name: 'HOST_IP',
        description:
          'IP address whose associated IDS alerts are being summarized',
      },
    ],
    query: `#path=conn
| id.orig_h=\${HOST_IP} OR id.resp_h=\${HOST_IP}
| split(service)
| case {
   local_orig = true and local_resp = true
   | direction := "east_west";
   local_orig = true and local_resp = false
   | direction := "north_south_outbound";
   local_orig = false and local_resp = true
   | direction := "north_south_inbound";
   local_orig = false and local_resp = false
   | direction := "external_external";
   *
   | direction := unknown;
}
| case {
   id.orig_h = \${HOST_IP}
   | remote_ip := id.resp_h
   | entity_role := "client";
   id.resp_h = \${HOST_IP}
   | remote_ip := id.orig_h
   | entity_role := "server";
   *
   | remote_ip := "unknown"
   | entity_role := unknown;
}
| groupBy(
   field=[direction, entity_role, remote_ip],
   function=[
   count(as=connection_cnt),
   collect(proto),
   collect(service),
   collect(id.resp_p, limit=10),
   selectLast(ts),
   sum(orig_bytes, as=total_orig_bytes),
   sum(resp_bytes, as=total_resp_bytes),
   avg(duration, as=avg_duration),
   sum(duration, as=time_active)
   ]
)
| rename(field=[[ts,last_seen],[total_orig_bytes,total_client_bytes], [total_resp_bytes,total_server_bytes]])
| sort(connection_cnt, order=desc, limit=200)`,
  },
  {
    id: 'remote-access-tunnel-usage',
    question:
      'What remote access or tunneling protocol has this host been using?',
    description:
      'Track remote access and tunneling sessions along with authentication outcomes.',
    sources: ['ssh', 'rdp', 'tunnel'],
    parameters: [
      {
        name: 'HOST_IP',
        description:
          'IP address whose associated IDS alerts are being summarized',
      },
    ],
    query: `in(#path, values=[ssh, rdp, tunnel])
| id.orig_h=\${HOST_IP} OR id.resp_h=\${HOST_IP}
| case {
   id.orig_h = \${HOST_IP}
   | remote_ip := id.resp_h
   | role := "client";
   id.resp_h = \${HOST_IP}
   | remote_ip := id.orig_h
   | role := "server";
   *
   | remote_ip := "unknown"
   | role := unknown;
}
| groupBy(field=[
       remote_ip,
       role,
       auth_success,
       #path
   ],
   function=[
       count(as=connection_cnt),
       selectLast(ts)
   ]
)
| rename(ts, as=last_seen)
| sort(connection_cnt, order=desc)`,
  },
];

/**
 * Get all pivot questions (just the question text)
 */
export const getPivotQuestions = (): string[] => {
  return PIVOT_QUERIES.map((query: PivotQuery) => query.question);
};

/**
 * Find a pivot query by ID
 */
export const getPivotQueryById = (id: string): PivotQuery | undefined => {
  return PIVOT_QUERIES.find((query: PivotQuery) => query.id === id);
};

/**
 * Find a pivot query by question text
 */
export const getPivotQueryByQuestion = (
  question: string,
): PivotQuery | undefined => {
  return PIVOT_QUERIES.find((query: PivotQuery) => query.question === question);
};

/**
 * Substitute parameters in a pivot query
 * @example
 * const query = substitutePivotQueryParameters(
 *   getPivotQueryById('dhcp-lease-history')!.query,
 *   { IP_ADDRESS: '192.168.1.100' }
 * );
 */
export const substitutePivotQueryParameters = (
  query: string,
  parameters: Record<string, string>,
): string => {
  let substitutedQuery = query;
  Object.entries(parameters).forEach(([key, value]) => {
    const pattern = '\\\$\\\{' + key + '\\\}';
    substitutedQuery = substitutedQuery.replace(
      new RegExp(pattern, 'g'),
      value,
    );
  });
  return substitutedQuery;
};
